/**
 * The People list (view, filters, tag, sort, search) a person's record was opened from, carried
 * in the record's URL as `?from=`, so deleting them lands back on that same list, filters restored
 * (NAVIGATION_AND_STATES.md § Where you land after an action: "Delete a record → the L1 it came
 * from, filters restored"). In the URL rather than browser storage (decided in #7044): it holds for
 * a record opened in a new tab, a reload and back/forward, as the spec asks of list state.
 */

/** The People L1 lists whose rows open a person's record. */
const PEOPLE_LIST_PATHS = new Set([
  "/people",
  "/people/all",
  "/community",
  "/people/visitor-followup",
  "/people/life-events",
  "/people/engagement-scoring",
  "/people/settings/engagement",
  "/community/organizations",
  "/community/registration-approvals",
  "/community/merge",
]);

/**
 * `url` when it's a People list URL, else null: never a record page, and never another origin
 * (a URL like `//host/people` or `https://…` has a path no list has).
 */
export function peopleListUrl(url: string | null): string | null {
  if (url === null) return null;
  const queryAt = url.search(/[?#]/);
  const path = queryAt === -1 ? url : url.slice(0, queryAt);
  // `/people/` is the same list as `/people`; the rest of the URL (query, hash) is kept.
  const listPath = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return PEOPLE_LIST_PATHS.has(listPath) ? `${listPath}${queryAt === -1 ? "" : url.slice(queryAt)}` : null;
}

/** `recordHref` carrying the list it's opened from, for the record to return to after a delete. */
export function withReturnList(recordHref: string, listUrl: string): string {
  const parsed = new URL(recordHref, "http://forge.invalid");
  const list = peopleListUrl(listUrl);
  if (list) parsed.searchParams.set("from", list);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Router state for going back to the People list entry a record was opened from in this tab
 * (`fromListEntry`): the list puts back that entry's scroll position and, if it keeps one, its
 * selection (any selectable list reads `restoreSelectionFrom`), less `removedPersonId` or
 * `removedHouseholdId` when the way back removed that record. Undefined without an entry.
 */
export function listReturnState(
  fromListEntry: string | null,
  removedPersonId?: string,
  removedHouseholdId?: string,
):
  | { restoreSelectionFrom: string; restoreScrollFrom: string; removedPersonId?: string; removedHouseholdId?: string }
  | undefined {
  if (!fromListEntry) return undefined;
  return {
    restoreSelectionFrom: fromListEntry,
    restoreScrollFrom: fromListEntry,
    ...(removedPersonId ? { removedPersonId } : {}),
    ...(removedHouseholdId ? { removedHouseholdId } : {}),
  };
}

const RECORD_LIST_ENTRIES_KEY = "forge:people:record-list-entries";
/** Newest record visits kept; older ones can't be reached by a plausible run of Back presses. */
const RECORD_LIST_ENTRY_LIMIT = 50;

type RecordListEntries = Record<string, { personId: string; listEntry: string }>;

function readRecordListEntries(): RecordListEntries {
  try {
    const saved = JSON.parse(sessionStorage.getItem(RECORD_LIST_ENTRIES_KEY) ?? "null");
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch {
    return {};
  }
}

/**
 * The list entry a record's history entry (the router's location key) had, for this person. A
 * record's own navigation (its tabs, their redirects) carries no router state, so Back to one of
 * those entries finds its list entry here; a different person on the same key never does.
 */
export function readRecordListEntry(historyKey: string, personId: string | undefined): string | null {
  const saved = readRecordListEntries()[historyKey];
  return personId && saved && saved.personId === personId && typeof saved.listEntry === "string"
    ? saved.listEntry
    : null;
}

export function writeRecordListEntry(historyKey: string, personId: string, listEntry: string) {
  try {
    const all = readRecordListEntries();
    delete all[historyKey]; // re-insert as newest
    all[historyKey] = { personId, listEntry };
    const keys = Object.keys(all);
    keys.slice(0, Math.max(0, keys.length - RECORD_LIST_ENTRY_LIMIT)).forEach((key) => delete all[key]);
    sessionStorage.setItem(RECORD_LIST_ENTRIES_KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable (private mode): only the in-page memory of the entry is kept.
  }
}

const RETURN_LIST_KEY = "forge:people:return-list";

// This page load's id. The stored list names the load that wrote it, and a list from another one
// isn't read: sessionStorage outlives a full-page navigation (leaving Forge and coming back in the
// tab, a reload), which PeopleReturnListTracker never sees, so a record loaded fresh at a plain URL
// would otherwise return to a list from before it (Codex, PR #7043 round 31). A record opened in
// a new tab or reloaded has its `?from=` for that.
const DOCUMENT_ID =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

/** The People list this tab is working from: its URL and history entry. */
export type ReturnList = { url: string; entry: string | null };

/**
 * The tab's current return list (decided on PR #7043, round 25). Every People L1 records itself
 * as it's shown, a record opened with `?from=` records that, and a record reached without one
 * (from another record, a search, a link) returns here. So no link has to carry the list, and a
 * link added later is covered. Cleared when the tab leaves the People area, and only read in the
 * page load and the organization that wrote it, so a record opened from elsewhere, loaded fresh,
 * or reached after switching organization in place (which doesn't navigate) never inherits an old
 * list (Codex, PR #7043 round 33). `?from=` stays for a record opened in a new tab.
 */
export function readReturnList(organizationId: string | null | undefined): ReturnList | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(RETURN_LIST_KEY) ?? "null");
    if (saved?.doc !== DOCUMENT_ID) return null;
    if (!organizationId || saved?.org !== organizationId) return null;
    const url = peopleListUrl(typeof saved?.url === "string" ? saved.url : null);
    return url ? { url, entry: typeof saved.entry === "string" ? saved.entry : null } : null;
  } catch {
    return null;
  }
}

export function writeReturnList(list: ReturnList | null, organizationId?: string | null) {
  try {
    if (list) {
      sessionStorage.setItem(
        RETURN_LIST_KEY,
        JSON.stringify({ ...list, doc: DOCUMENT_ID, org: organizationId ?? null }),
      );
    } else {
      sessionStorage.removeItem(RETURN_LIST_KEY);
    }
  } catch {
    // Storage unavailable (private mode): records fall back to `?from=` and plain People.
  }
}

/** A People L1 or a person, household or family group record: where the return list holds. */
export function isPeopleAreaPath(pathname: string): boolean {
  return peopleListUrl(pathname) !== null || /^\/(people|households|family-groups)\/[^/]+/.test(pathname);
}

/** Where a delete on the record lands: the `?from=` list, or `fallback` when there's none. */
export function peopleListReturnUrl(from: string | null, fallback: string): string {
  return peopleListUrl(from) ?? fallback;
}

/**
 * `url` carrying the viewer's `?ui=` preview param when it has none of its own: with browser
 * storage blocked, the param is the only thing keeping a preview viewer on the new layout.
 */
export function withUiParam(url: string, ui: string | null): string {
  if (!ui) return url;
  const parsed = new URL(url, "http://forge.invalid");
  if (parsed.searchParams.has("ui")) return url;
  parsed.searchParams.set("ui", ui);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
